import { useEffect, useState } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import orderBy from 'lodash/orderBy';
import gettext, { ngettext } from '@sdk/lang/gettext';
import Row from '@sdk/components/ui/spacing/Row';
import Column from '@sdk/components/ui/spacing/Column';
import HorizontalSeparator from '@sdk/components/ui/HorizontalSeparator';
import Text14 from '@sdk/components/ui/typography/Text14';
import Text16 from '@sdk/components/ui/typography/Text16';
import Button from '@sdk/components/ui/buttons/Button';
import Table from '@sdk/components/ui/table/Table';
import TableHeader from '@sdk/components/ui/table/TableHeader';
import TableHeaderCell from '@sdk/components/ui/table/TableHeaderCell';
import TableRow from '@sdk/components/ui/table/TableRow';
import TableCell from '@sdk/components/ui/table/TableCell';
import useSlingApi from '@sdk/api/useSlingApi';
import useAuth from '@sdk/authentication/useAuth';
import useMounted from '@sdk/utils/hooks/useMounted';
import dateToHumanFormat from '@sdk/utils/dateFormat/dateToHumanFormat';
import formatTime from '@sdk/utils/dateFormat/formatTime';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import type { AccountSession } from '@sdk/authentication/types/apiTypes';

import SettingsSection from './components/SettingsSection';

const HeaderRow = styled(Row)`
  align-items: center;
  justify-content: space-between;
`;

const ButtonContainer = styled(Row)`
  width: 185px;
`;

const ButtonRow = styled(Row)`
  flex: 1;
  align-items: center;
  justify-content: center;
`;

export const AccountActivityScreen: React.FC<React.PropsWithChildren<unknown>> = () => {
  const [loadSessionsError, setLoadSessionsError] = useState('');
  const [sessions, setSessions] = useState<AccountSession[]>([]);
  const mountedRef = useMounted();
  const api = useSlingApi();
  const { currentUser } = useAuth();
  const title = ngettext('%(count)s Session', '%(count)s Sessions', sessions.length, {
    count: sessions.length,
  });

  useEffect(() => {
    (async (): Promise<void> => {
      try {
        const { data } = await api.auth.getAllSessions();
        setSessions(orderBy(data, ['current', 'id'], ['desc', 'desc']));
      } catch ({ data }) {
        if (mountedRef.current) {
          setLoadSessionsError(data.message);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signOutAll = async (): Promise<void> => {
    try {
      await api.auth.deleteAllSessions();
      const currentSession = sessions.find(({ current }) => current);
      setSessions([currentSession]);
    } catch (e) {
      console.info('error while deleting sessions', e);
    }
  };

  const signOutSession = async (session: AccountSession): Promise<void> => {
    try {
      await api.auth.deleteSession(session);
      const updatedSessions = sessions.filter(({ id }) => id !== session.id);
      setSessions(updatedSessions);
    } catch (e) {
      console.info('error while deleting session', e);
    }
  };

  if (loadSessionsError) {
    return <Text14 themeColor={ThemeColorType.danger}>{loadSessionsError}</Text14>;
  }

  return (
    <Column>
      <HeaderRow mRight={20}>
        <Text16 semiBold themeColor={ThemeColorType.primary}>
          {title}
        </Text16>
        {sessions.length > 1 && (
          <ButtonContainer>
            <Button onClick={signOutAll} buttonType={ButtonType.primary}>
              {gettext('Sign out all other sessions')}
            </Button>
          </ButtonContainer>
        )}
      </HeaderRow>
      <Row top={10} bottom={20}>
        <HorizontalSeparator />
      </Row>
      <SettingsSection pBottom={20}>
        <Table>
          <TableHeader>
            <TableHeaderCell label="#" />
            <TableHeaderCell label={gettext('ip address')} />
            <TableHeaderCell label={gettext('browser')} />
            <TableHeaderCell label={gettext('last active')} />
            <TableHeaderCell label="" />
          </TableHeader>
          <tbody>
            {sessions.map((session) => (
              <TableRow key={session.id}>
                <TableCell>
                  <Text14 themeColor={ThemeColorType.gray}>{session.id}</Text14>
                </TableCell>
                <TableCell>
                  <Text14 themeColor={ThemeColorType.gray}>{session.ip}</Text14>
                </TableCell>
                <TableCell>
                  <Text14 themeColor={ThemeColorType.gray}>{session.browser}</Text14>
                </TableCell>
                <TableCell>
                  <Text14 themeColor={ThemeColorType.gray}>
                    {dateToHumanFormat(session.lastseen)}&nbsp;
                    {gettext('at')}&nbsp;
                    {formatTime(new Date(session.lastseen), currentUser.settings.usTimeFormat)}
                  </Text14>
                </TableCell>
                <TableCell>
                  {session.current && (
                    <ButtonRow>
                      <Text14 themeColor={ThemeColorType.gray}>{gettext('Current')}</Text14>
                    </ButtonRow>
                  )}
                  {!session.current && (
                    <Button
                      onClick={(): void => {
                        signOutSession(session);
                      }}>
                      {gettext('Sign out')}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </tbody>
        </Table>
      </SettingsSection>
    </Column>
  );
};
